import { SLIDER_BASE_CLASS } from "../constants.js";

export default class LocationSearchSlider {
  constructor(container, store, eventBus) {
    this.container = container;
    this.store = store;
    this.eventBus = eventBus;

    this.slider = this.container?.querySelector("input[type='range']");
    this.sliderPoints = this.container?.querySelector(`.${SLIDER_BASE_CLASS}__points`);
    this.valueInput = this.container?.querySelector(`.${SLIDER_BASE_CLASS}__value-input`);
    this.valueContainer = this.container?.querySelector(`.${SLIDER_BASE_CLASS}__value-container`);
    this.datalist = this.container?.querySelector(`.${SLIDER_BASE_CLASS}__datalist`);
    this.markers = this.container?.querySelector(`.${SLIDER_BASE_CLASS}__markers`);
    this.maxValueLabel = this.container?.querySelector(`.${SLIDER_BASE_CLASS}__max-value`);
    this.eventLabel = this.container?.querySelector(`.${SLIDER_BASE_CLASS}__event`);

    this.addEventListeners();
    this.initializeSlider();
  }

  addEventListeners() {
    if (this.slider) {
      this.slider.addEventListener("input", this.handleSliderInput.bind(this));
      this.slider.addEventListener("change", this.trackAnalytics.bind(this));
    }

    if (this.valueInput) {
      this.valueInput.addEventListener("input", this.handleInputChange.bind(this));
      this.valueInput.addEventListener("blur", this.handleInputBlur.bind(this));
      this.valueInput.addEventListener("keydown", this.handleInputKeydown.bind(this));
    }

    this.eventBus.on("locationTypeFiltersChanged", this.updateSlider.bind(this));
    this.eventBus.on("locationSelectedLineupChanged", this.updateSlider.bind(this));
    this.eventBus.on("locationMinimumCapacityChanged", this.updateSlider.bind(this));

    window.addEventListener("resize", this.updateSlider.bind(this));
  }

  initializeSlider() {
    this.updateSlider();
    this.resetSlider();
  }

  updateSlider() {
    this.toggleSliderVisibility();
    this.updateEventLabel();
    
    const currentValue = this.store.minimumCapacity;
    this.setSliderMinMax();
    
    const maxCapacity = this.store.getMaximumCapacity();
    if (currentValue > maxCapacity) {
      this.store.setMinimumCapacity(maxCapacity);
    }
    
    this.updateSliderValue();
  }

  toggleSliderVisibility() {
    this.container?.classList.toggle("d-none", !this.store.activeTypeValue);
  }

  updateEventLabel() {
    if (this.eventLabel) {
      this.eventLabel.textContent = this.store.activeTypeValue
        ? this.store.activeTypeValue.toLowerCase()
        : "event";
    }
  }

  setSliderMinMax() {
    const maxCapacity = this.store.getMaximumCapacity();

    if (this.slider) {
      this.slider.min = 0;
      this.slider.max = maxCapacity;
    }

    if (this.valueInput) {
      this.valueInput.min = 0;
      this.valueInput.max = maxCapacity;
    }

    this.updateSliderPoints();
  }

  updateSliderPoints() {
    if (!this.sliderPoints) return;
    const capacities = this.store.getCapacities();
    this.sliderPoints.innerHTML = "";
    capacities.forEach(() => {
      const point = document.createElement("div");
      point.classList.add(`${SLIDER_BASE_CLASS}__point`);
      this.sliderPoints.appendChild(point);
    });
  }

  resetSlider() {
    if (this.slider) {
      this.slider.value = 0;
    }
    if (this.valueInput) {
      this.valueInput.value = 0;
    }
    this.store.setMinimumCapacity(0);
    this.updateSliderValue();
  }

  handleSliderInput() {
    if (!this.slider || !this.valueInput) return;
    this.valueInput.value = this.slider.value;
    this.updateCapacityValue(this.slider.value);
  }

  handleInputChange() {
    if (!this.valueInput || !this.slider) return;
    const value = parseInt(this.valueInput.value);
    
    const min = parseInt(this.valueInput.min) || 0;
    const max = parseInt(this.valueInput.max) || 100;
    
    this.slider.value = isNaN(value) ? min : Math.max(min, Math.min(max, value));;
    
    this.updateCapacityValue(value);
  }

  handleInputBlur() {
    if (!this.valueInput) return;
    
    if (this.valueInput.value === '' || isNaN(parseInt(this.valueInput.value, 10))) {
      this.valueInput.value = this.valueInput.min || 0;
      this.updateCapacityValue(this.valueInput.value);
    }

    this.trackAnalytics();
  }

  handleInputKeydown(event) {
    if (event.key === 'Enter') {
      this.valueInput.blur();
    }
  }

  updateCapacityValue(value) {
    const numValue = parseInt(value, 10);
    
    if (this.store.minimumCapacity !== numValue) {
      this.store.setMinimumCapacity(numValue);
    }
    
    this.positionValueInput();
    this.updateMaxValueLabel();
    this.updateDatalistAndMarkers();
  }

  updateSliderValue() {
    if (!this.slider || !this.valueInput) return;
  
    const capacityValue = this.store.minimumCapacity;
    this.slider.value = capacityValue;

    this.valueInput.value = window.innerWidth < 768 && capacityValue === 0 ? '' : capacityValue;
    this.valueInput.placeholder = window.innerWidth < 768 && capacityValue === 0 ? '0' : '';
  
    this.positionValueInput();
    this.updateMaxValueLabel();
    this.updateDatalistAndMarkers();
  }

  positionValueInput() {
    if (!this.valueContainer || !this.slider) return;
    const isMobile = window.innerWidth < 768;
    
    if (isMobile) {
      this.valueContainer.style.left = '';
      return;
    }
    
    const sliderWidth = this.slider.offsetWidth;
    const thumbWidth = 20;
    const min = parseInt(this.slider.min, 10) || 0;
    const max = parseInt(this.slider.max, 10) || 1;
    const valuePosition = (this.slider.value - min) / (max - min);
    
    const leftPosition = Math.max(
      0,
      Math.min(
        sliderWidth - thumbWidth,
        valuePosition * sliderWidth - thumbWidth / 2
      )
    );
    
    this.valueContainer.style.left = `${leftPosition}px`;
  }

  updateMaxValueLabel() {
    if (!this.maxValueLabel || !this.slider) return;
    
    const maxCapacity = this.store.getMaximumCapacity();
    this.maxValueLabel.textContent = maxCapacity;
    const valuePosition =
      (this.slider.value - this.slider.min) /
      (this.slider.max - this.slider.min);
    this.maxValueLabel.classList.toggle(
      `${SLIDER_BASE_CLASS}__max-value--active`,
      valuePosition < 0.95
    );
  }

  updateDatalistAndMarkers() {
    if (!this.datalist || !this.markers || !this.slider) return;
    const capacities = this.store.getCapacities();

    this.datalist.innerHTML = "";
    this.markers.innerHTML = "";

    capacities.forEach((capacity) => {
      const offset = this.calculateOffset(capacity);

      const option = document.createElement("option");
      option.value = capacity;
      option.style.left = `calc(${offset - 4}px)`;
      this.datalist.appendChild(option);

      const marker = document.createElement("div");
      marker.classList.add(`${SLIDER_BASE_CLASS}__marker`);
      marker.style.left = `calc(${offset - 4}px)`;
      this.markers.appendChild(marker);
    });
  }

  calculateOffset(capacity) {
    const sliderWidth = this.slider.offsetWidth;
    const thumbWidth = 20;
    const min = parseInt(this.slider.min, 10) || 0;
    const max = parseInt(this.slider.max, 10) || 1;
    const ratio = (capacity - min) / (max - min);
    return ratio * (sliderWidth - thumbWidth) + thumbWidth / 2;
  }

  trackAnalytics() {
    this.store.analytics.trackSearchResults();
  }
}